/**
 * Motor de Cálculo de Ponto de Equilíbrio Operacional
 * R-MAX Precificação Inteligente
 * 
 * Regra:
 * Meta de Vendas = Teto(Despesas Fixas / Sobra Bruta Unitária)
 * Se Sobra Bruta Unitária <= 0, o cálculo é bloqueado (evita Infinity, NaN ou números negativos)
 */

export interface BreakEvenInput {
  fixedCosts: number;
  unitContributionMargin: number;
  workingDaysPerMonth?: number;
}

export type BreakEvenStatus = 'VALID' | 'MARGIN_NON_POSITIVE' | 'FIXED_COSTS_ZERO_OR_NEGATIVE';

export interface BreakEvenResult {
  isValid: boolean;
  status: BreakEvenStatus;
  breakEvenUnits: number;
  dailyUnits: number;
  revenueAtBreakEven: number;
  message?: string;
}

export function calculateBreakEven({
  fixedCosts,
  unitContributionMargin,
  workingDaysPerMonth = 22,
}: BreakEvenInput, unitPrice = 0): BreakEvenResult {
  // Higienização de entradas numéricas
  const safeFixed = Number.isFinite(fixedCosts) ? Math.max(0, fixedCosts) : 0;
  const safeMargin = Number.isFinite(unitContributionMargin) ? unitContributionMargin : 0;
  const safeDays = workingDaysPerMonth > 0 ? workingDaysPerMonth : 22;

  if (safeMargin <= 0) {
    return {
      isValid: false,
      status: 'MARGIN_NON_POSITIVE',
      breakEvenUnits: 0,
      dailyUnits: 0,
      revenueAtBreakEven: 0,
      message: 'Com a configuração atual, cada venda gera prejuízo ou margem zero. Ajuste o preço de venda ou reduza a carga tributária antes de calcular o Ponto de Equilíbrio.',
    };
  }

  if (safeFixed <= 0) {
    return {
      isValid: false,
      status: 'FIXED_COSTS_ZERO_OR_NEGATIVE',
      breakEvenUnits: 0,
      dailyUnits: 0,
      revenueAtBreakEven: 0,
      message: 'Informe o valor mensal das despesas fixas da sua empresa para calcular a meta de vendas.',
    };
  }

  const breakEvenUnits = Math.ceil(safeFixed / safeMargin);
  const dailyUnits = Math.ceil(breakEvenUnits / safeDays);
  const revenueAtBreakEven = breakEvenUnits * Math.max(0, unitPrice);

  return {
    isValid: true,
    status: 'VALID',
    breakEvenUnits,
    dailyUnits,
    revenueAtBreakEven,
  };
}
